"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { storyblokEditable, StoryblokComponent } from "@storyblok/react/rsc";
import Autoplay from "embla-carousel-autoplay";
import { useLightboxOpen } from "@/components/layout/LightboxProvider";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  GalleryNavPrevious,
  GalleryNavNext,
  type CarouselApi,
} from "@/components/ui/carousel";

type GalleryBackgroundColor = "none" | "white" | "sand-100" | "sky-50";

const backgroundColorClasses: Record<GalleryBackgroundColor, string> = {
  none: "bg-transparent",
  white: "bg-white",
  "sand-100": "bg-sand-100",
  "sky-50": "bg-sky-50",
};

type TestimonialGalleryStoryblok = {
  headline?: string;
  testimonials?: any[];
  background_color?: GalleryBackgroundColor;
  _uid: string;
  component: "testimonial_gallery";
  [k: string]: any;
};

type TestimonialGalleryProps = {
  blok: TestimonialGalleryStoryblok;
};

const TestimonialGallery = ({ blok }: TestimonialGalleryProps) => {
  const [api, setApi] = useState<CarouselApi>();
  const [canScroll, setCanScroll] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const lightboxOpen = useLightboxOpen();
  const autoplayRef = useRef(
    Autoplay({
      delay: 3000,
      stopOnInteraction: true,
      stopOnMouseEnter: true,
    }),
  );

  const bgColor = blok.background_color || "none";
  const bgClass = backgroundColorClasses[bgColor];

  const checkCanScroll = useCallback(() => {
    if (!api) return;
    setCanScroll(api.canScrollNext() || api.canScrollPrev());
  }, [api]);

  useEffect(() => {
    if (!api) return;
    checkCanScroll();
    api.on("resize", checkCanScroll);
    api.on("reInit", checkCanScroll);
    return () => {
      api.off("resize", checkCanScroll);
      api.off("reInit", checkCanScroll);
    };
  }, [api, checkCanScroll]);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const autoplay = autoplayRef.current;
    if (!api) return;
    if (canScroll && isInView && !lightboxOpen) {
      autoplay.play();
    } else {
      autoplay.stop();
    }
  }, [api, canScroll, isInView, lightboxOpen]);

  return (
    <div ref={sectionRef} {...storyblokEditable(blok)} className="w-full ">
      <section
        className={`${bgClass} py-8 md:py-12 flex flex-col gap-4 w-full items-center rounded-3xl md:rounded-none`}
      >
        {blok.headline && (
          <h2 className="text-headline-xl text-black text-center w-full px-4 md:px-8 mb-2">
            {blok.headline}
          </h2>
        )}

        <Carousel
          setApi={setApi}
          opts={{
            align: "center",
            loop: false,
            dragFree: false,
            watchResize: true,
            breakpoints: {
              "(min-width: 768px)": {
                align: "start",
                dragFree: true,
              },
            },
          }}
          plugins={[autoplayRef.current]}
          className="w-full relative"
        >
          <GalleryNavPrevious show={canScroll} />
          <CarouselContent
            className={
              canScroll
                ? "md:ml-0 md:pl-8 items-stretch"
                : "justify-center items-stretch"
            }
          >
            {blok.testimonials?.map((nestedBlok: any) => (
              <CarouselItem
                key={nestedBlok._uid}
                className={
                  canScroll
                    ? "basis-[85%] md:basis-auto md:pl-4 pl-3 h-auto"
                    : "basis-[85%] md:basis-auto px-2 h-auto"
                }
              >
                <StoryblokComponent blok={nestedBlok} />
              </CarouselItem>
            ))}
            {canScroll && <div className="shrink-0 w-px md:w-8" />}
          </CarouselContent>
          <GalleryNavNext show={canScroll} />
        </Carousel>
      </section>
    </div>
  );
};

export default TestimonialGallery;
